import React, { FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import styled from 'styled-components';
import { rem } from 'polished';

import { Col, Row } from '../Grid';
import { useTranslations } from '@hooks/useTranslations';
import useCustomRouter from '@hooks/useCustomRouter';
import useCalculateTextHeightPerWidth from '@hooks/useCalculateTextHeightPerWidth';
import { StockModelListProps } from '.';
import StockCard from '../StockModelCard';
import FilterDuck from '../../redux/filters/filter.duck';
import { LastItemBannerContainer } from './StockModelListStyled';
import StockService from '../../services/stock/stock.service';
import { CarJourneyType } from '../../services';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import routes from '../../constants/routes';
import { EContextPage, hasPromoCodeOffersList } from '@utils/Promo.utils';
import { IVehicleTransformed } from '../../services/stock/models/stockItems.service.model';
import { BRAND, NEED_HAND_SETTING_KEY } from 'src/constants/main';
import { ContentDuck } from '../../redux/content/content.duck';
import { Redux } from '../../redux/redux.interface';
import getDealerNameAndCity from '@components/StockModelCard/utils/getDealerNameAndCity';
import { IDealer } from '../../interfaces/Dealer';
import { useMediaQuery } from '@hooks/useMediaquery';
import { useTheme } from '@hooks/useTheme';

const StyledCol = styled(Col)`
    margin-bottom: ${rem(15)};
    display: flex;
    justify-content: center;
    width: 100%;
    max-width: 450px;
`;

const LastItemBanner = ({
    isVisibleBanner,
    className,
}: {
    isVisibleBanner?: boolean;
    className: string | undefined;
}) => {
    const { t } = useTranslations();
    const router = useCustomRouter();

    const { modelId, bodyStyleId } = extractParamsFromPathname(router.asPath);

    return (
        isVisibleBanner && (
            <StyledCol xs={12} sm={6} md={6} lg={4}>
                <LastItemBannerContainer brand={BRAND} className={className}>
                    <div className="last-item-wrapper">
                        <div className="last-item-banner-container-text">
                            <h3>{t('stockModelPage.banner')}</h3>
                        </div>
                        <div className="last-item-banner-container-button">
                            <button
                                onClick={async () => {
                                    const handleFallbackRedirect = () => {
                                        router.push(
                                            buildParameterizedPathname(routes.ROOT, CarJourneyType.CONFIGURABLE)
                                        );
                                    };

                                    try {
                                        const { data } = await StockService.getTrimSlug(modelId, bodyStyleId);

                                        if (data?.redirectSlug) {
                                            router.push(
                                                `${buildParameterizedPathname(
                                                    routes.SELECTOR,
                                                    CarJourneyType.CONFIGURABLE
                                                )}${data.redirectSlug}`
                                            );
                                        } else {
                                            handleFallbackRedirect();
                                        }
                                    } catch {
                                        handleFallbackRedirect();
                                    }
                                }}
                            >
                                {t('stockModelPage.bannerButton')}
                            </button>
                        </div>
                    </div>
                    <div className="last-item-banner-container-image" />
                </LastItemBannerContainer>
            </StyledCol>
        )
    );
};

const StockModelListTemplate: FC<StockModelListProps> = ({
    className,
    vehicleList,
    vehicleOutList,
    showLastItemBanner,
}) => {
    const paymentJourney = useSelector((state) => FilterDuck.getPaymentJourneyType(state));

    const settings = useSelector((state: Redux) => ContentDuck.getSettings(state));
    const theme = useTheme();
    const needHandBlockOrder = Number(settings.find((setting) => setting.key === NEED_HAND_SETTING_KEY)?.value || 0);

    const availableVehicles = vehicleList.filter((vehicle) => vehicle?.orders[0]?.status === 'available');
    const bookedVehicles = vehicleList.filter((vehicle) => vehicle?.orders[0]?.status !== 'available');

    const unavailableVehicles = [...bookedVehicles, ...vehicleOutList];

    const hasOptions = (optionItems: Array<IVehicleTransformed>, index: number) => {
        switch (true) {
            case index % 3 === 0: {
                return Boolean(
                    optionItems[index]?.options.length ||
                        optionItems[index + 1]?.options.length ||
                        optionItems[index + 2]?.options.length
                );
            }
            case (index + 4) % 3 === 0: {
                return Boolean(
                    optionItems[index]?.options.length ||
                        optionItems[index - 1]?.options.length ||
                        optionItems[index - 2]?.options.length
                );
            }
            default:
                return Boolean(
                    optionItems[index]?.options.length ||
                        optionItems[index + 1]?.options.length ||
                        optionItems[index - 1]?.options.length
                );
        }
    };

    const all = [...availableVehicles, ...unavailableVehicles.map((item) => ({ ...item, isDisabled: true }))];

    const allItems = all.map((item, index) => ({
        ...item,
        hasPromoCode: hasPromoCodeOffersList(availableVehicles, index, EContextPage.trim),
        hasOptions: hasOptions(availableVehicles, index),
    }));

    return (
        <div className={className}>
            <Row
                id="skip-to-content"
                style={{ alignItems: 'stretch', justifyContent: 'center' }}
                className="model-page-wrapper"
            >
                {allItems.map((vehicle, index) => (
                    <>
                        {needHandBlockOrder > 0 && index + 1 === needHandBlockOrder && (
                            <StockCard
                                index={`needHand-${index}`}
                                key={`needHand-${vehicle.orders[0].uuid}`}
                                paymentJourney={paymentJourney}
                                vehicle={vehicle}
                                isNeedHandItem
                            />
                        )}
                        <>
                            <StockCard
                                index={index}
                                key={vehicle.orders[0].uuid}
                                paymentJourney={paymentJourney}
                                vehicle={vehicle}
                                disabled={(vehicle as { isDisabled?: boolean }).isDisabled}
                            />
                        </>
                        {needHandBlockOrder > allItems?.length && index + 1 === allItems?.length && (
                            <StockCard
                                index={`needHand-${index}`}
                                key={`needHand-${vehicle.orders[0].uuid}`}
                                paymentJourney={paymentJourney}
                                vehicle={vehicle}
                                isNeedHandItem
                            />
                        )}
                    </>
                ))}
                <LastItemBanner className={className} isVisibleBanner={showLastItemBanner} />
            </Row>
        </div>
    );
};

export default StockModelListTemplate;
